import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  FormProps,
  Input,
  Modal,
  Radio,
  Select,
  TimePicker,
} from "antd";
import {
  AlertCircle,
  Building,
  Calendar,
  Clock,
  DollarSign,
  Save,
  X,
} from "lucide-react";
import { FC, useEffect } from "react";
import { CustomizeRequiredMark } from "~/components/common/custom-required-mark";
import { useGetClients } from "~/services/client/client.queries";
import { Priority, TaskStatus, TaskType } from "~/utils/constants";
import { AssigneeSelect } from "./assignee-select";
import { Attachments } from "./attachments";
import { InventoriesSelect } from "./inventories-select";
import { TaskFormValues } from "./type";
import { useGetAllStaff } from "~/services/operation";
import { TaskPreview } from "./task-preview";
import { capitalize } from "~/utils";
import {
  TASK_KEYS,
  useCreateTask,
  useGetTask,
  useUpdateTask,
} from "~/services/task";
import dayjs from "dayjs";
import { useNotification } from "~/hooks";
import { useTaskStore } from "../list-tasks/task.store";
import { useQueryClient } from "@tanstack/react-query";

const getPriorityBadge = (priority: Priority) => {
  switch (priority) {
    case Priority.HIGH:
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
          High
        </span>
      );
    case Priority.MEDIUM:
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
          Medium
        </span>
      );
    case Priority.LOW:
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
          Low
        </span>
      );
    default:
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
          {priority}
        </span>
      );
  }
};

type Props = {};

export const CreateTaskModal: FC<Props> = () => {
  const { formState, closeTaskModal } = useTaskStore();
  const [form] = Form.useForm<TaskFormValues>();

  const { success, error } = useNotification();

  const { data: task } = useGetTask(formState.taskId);

  const { data: clients } = useGetClients();
  const { data: staffs } = useGetAllStaff();

  useEffect(() => {
    if (!formState.open) {
      form.resetFields();
      return;
    }
    if (!formState.taskId) {
      form.resetFields();
    } else if (task) {
      form.setFieldsValue({
        ...task,
        dueDate: task.dueDate ? dayjs(task.dueDate) : undefined,
        startDate: task.startDate ? dayjs(task.startDate) : undefined,
        startTime: task.startTime
          ? dayjs(`2025-03-25 ${task.startTime}`)
          : undefined,
        priority: task.priority,
        clientId: task.clientId ?? undefined,
        billable: task.billable,
        assigneeIds: task.assignees.map((assignee) => assignee.id),
        inventoryIds: task.inventories.map((inventory) => inventory.id),
        storageIds: [],
        files: [],
        taskType: Object.values(TaskType).includes(task.taskType as TaskType)
          ? task.taskType
          : TaskType.OTHER,
        customTaskType: !Object.values(TaskType).includes(
          task.taskType as TaskType
        )
          ? task.taskType
          : undefined,
      });
    }
  }, [form, formState.open, formState.taskId, task]);

  const mutation = useCreateTask();
  const mutationUpdate = useUpdateTask();
  const qc = useQueryClient();

  const handleSubmit: FormProps<TaskFormValues>["onFinish"] = (values) => {
    const formData = new FormData();
    const {
      inventoryIds,
      assigneeIds,
      dueDate,
      startDate,
      startTime,
      customTaskType,
      taskType,
      ...rest
    } = values;

    // Log input values for debugging
    console.log("Form values:", { taskType, customTaskType, ...rest });

    // Determine the final taskType
    const finalTaskType =
      taskType === TaskType.OTHER && customTaskType ? customTaskType : taskType;

    // Validate that customTaskType is provided when taskType is OTHER
    if (taskType === TaskType.OTHER && !customTaskType) {
      form.setFields([
        {
          name: "customTaskType",
          errors: ["Custom task type is required"],
        },
      ]);
      console.log(
        "Validation failed: customTaskType is required when taskType is OTHER"
      );
      return;
    }

    // Add fields to FormData, explicitly setting taskType
    formData.append("taskType", finalTaskType);
    console.log("Final taskType:", finalTaskType);

    Object.keys(rest).forEach((_key) => {
      const key = _key as keyof TaskFormValues;
      if (key === "files") {
        if (values[key] && Array.isArray(values[key])) {
          values[key].forEach((file: File) => {
            formData.append("files", file);
          });
        }
      } else if (values[key] !== null && values[key] !== undefined) {
        if (typeof values[key] === "object" && !(values[key] instanceof File)) {
          formData.append(key, JSON.stringify(values[key]));
        } else if (typeof values[key] === "boolean") {
          formData.append(key, values[key].toString());
        } else {
          formData.append(key, values[key] as string | Blob);
        }
      }
    });

    if (inventoryIds && inventoryIds.length > 0) {
      inventoryIds.forEach((inventoryId) => {
        formData.append("inventoryIds[]", inventoryId);
      });
    }

    if (assigneeIds && assigneeIds.length > 0) {
      assigneeIds.forEach((assigneeId) => {
        formData.append("assigneeIds[]", assigneeId);
      });
    }

    if (dueDate) {
      formData.append("dueDate", dueDate.toISOString());
    }
    if (startDate) {
      formData.append("startDate", startDate.toISOString());
    }
    if (startTime) {
      formData.append("startTime", dayjs(startTime).format("HH:mm:ss"));
    }

    formData.append("taskStatus", TaskStatus.PENDING);

    const formDataEntries: [string, FormDataEntryValue][] = [];
    formData.forEach((value, key) => {
      formDataEntries.push([key, value]);
    });
    console.log("FormData entries:", formDataEntries);

    if (formState.taskId) {
      mutationUpdate.mutate(
        { taskId: formState.taskId, payload: formData },
        {
          onSuccess: () => {
            form.resetFields();
            qc.invalidateQueries({
              queryKey: TASK_KEYS.GET_TASKS({}),
            });
            success({
              message: "Task updated successfully",
              description: "The task has been updated successfully.",
            });
            closeTaskModal();
          },
          onError: (err) => {
            console.error("Error updating task:", err);
            error({
              message: "Error updating task",
              description:
                "There was an error updating the task. Please try again.",
            });
          },
        }
      );
    } else {
      mutation.mutate(formData, {
        onSuccess: () => {
          form.resetFields();
          qc.invalidateQueries({
            queryKey: TASK_KEYS.GET_TASKS({}),
          });
          success({
            message: "Task created successfully",
            description: "The task has been created successfully.",
          });
          closeTaskModal();
        },
        onError: (err) => {
          console.error("Error creating task:", err);
          error({
            message: "Error creating task",
            description:
              "There was an error creating the task. Please try again.",
          });
        },
      });
    }
  };

  return (
    <Modal
      open={formState.open}
      footer={null}
      onCancel={() => closeTaskModal()}
      width={"70%"}
      closeIcon={null}
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Create Internal Task</h1>
        <div className="flex space-x-2">
          <Button
            onClick={closeTaskModal}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <X size={16} />
            <span>Cancel</span>
          </Button>
          <Button
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm"
            onClick={() => form.submit()}
            form="task-form"
            loading={
              formState.taskId ? mutationUpdate.isPending : mutation.isPending
            }
          >
            <Save size={16} />
            <span>Save Task</span>
          </Button>
        </div>
      </div>

      {/* Form */}
      <Form
        form={form}
        onFinish={handleSubmit}
        requiredMark={CustomizeRequiredMark}
        layout="vertical"
        size="large"
        id="task-form"
      >
        <div className="bg-white rounded-lg border shadow-sm mb-6">
          <div className="p-4 border-b">
            <h2 className="font-medium">Task Details</h2>
          </div>

          <div className="p-6 space-y-6">
            {/* Basic Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Form.Item
                name="title"
                rules={[{ required: true, message: "Task title is required" }]}
                label="Task Title"
                className="mb-0"
              >
                <Input />
              </Form.Item>
              <div>
                <Form.Item
                  name="taskType"
                  rules={[{ required: true, message: "Task type is required" }]}
                  label="Task Type"
                  className="mb-0"
                >
                  <Select
                    options={Object.values(TaskType).map((type) => ({
                      label: capitalize(type),
                      value: type,
                    }))}
                  />
                </Form.Item>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.taskType !== currentValues.taskType
                  }
                >
                  {({ getFieldValue }) =>
                    getFieldValue("taskType") === TaskType.OTHER ? (
                      <Form.Item
                        name="customTaskType"
                        rules={[
                          {
                            required: true,
                            message: "Custom task type is required",
                          },
                        ]}
                        className="mt-4"
                      >
                        <Input placeholder="Enter custom task type" />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>
              </div>
            </div>

            {/* Description */}
            <div>
              <Form.Item
                name="description"
                label="Description"
                className="mb-0"
              >
                <Input.TextArea rows={3} />
              </Form.Item>
            </div>

            {/* Schedule */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label
                  htmlFor="startDate"
                  className="block text-sm font-medium text-gray-700"
                >
                  Start Date
                </label>
                <div className="flex items-center">
                  <Calendar size={16} className="text-gray-400 mr-2" />
                  <Form.Item name="startDate" className="mb-0" noStyle>
                    <DatePicker className="w-full" />
                  </Form.Item>
                </div>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="dueDate"
                  className="block text-sm font-medium text-gray-700"
                >
                  Due Date <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center">
                  <Calendar size={16} className="text-gray-400 mr-2" />
                  <Form.Item
                    name="dueDate"
                    className="mb-0"
                    rules={[
                      { required: true, message: "Due date is required" },
                    ]}
                    noStyle
                  >
                    <DatePicker className="w-full" />
                  </Form.Item>
                </div>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="startTime"
                  className="block text-sm font-medium text-gray-700"
                >
                  Start Time
                </label>
                <div className="flex items-center">
                  <Clock size={16} className="text-gray-400 mr-2" />
                  <Form.Item name="startTime" className="mb-0" noStyle>
                    <TimePicker className="w-full" />
                  </Form.Item>
                </div>
              </div>
            </div>

            {/* Priority */}
            <div>
              <Form.Item
                name="priority"
                rules={[{ required: true, message: "Priority is required" }]}
                label="Priority"
                className="mb-0"
              >
                <Radio.Group
                  options={Object.values(Priority).map((priority) => ({
                    label: getPriorityBadge(priority),
                    value: priority,
                  }))}
                />
              </Form.Item>
            </div>
          </div>
        </div>

        {/* Assignment Section */}
        <div className="bg-white rounded-lg border shadow-sm mb-6">
          <div className="p-4 border-b">
            <h2 className="font-medium">Assignment</h2>
          </div>

          <div className="p-6 space-y-6">
            <div className="space-y-2">
              <Form.Item name="assigneeIds" label="Assign To" className="mb-0">
                <AssigneeSelect />
              </Form.Item>
            </div>
          </div>
        </div>

        {/* Client & Billing Section */}
        <div className="bg-white rounded-lg border shadow-sm mb-6">
          <div className="p-4 border-b">
            <h2 className="font-medium">Client & Billing</h2>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Form.Item label="Client" className="mb-0">
                  <div className="flex items-center">
                    <Building size={16} className="text-gray-400 mr-2" />
                    <Form.Item name="clientId" className="mb-0" noStyle>
                      <Select
                        options={[
                          { value: "", label: "No client (internal task)" },
                          ...(clients?.items?.map((client) => ({
                            value: client.id,
                            label:
                              client.companyName ||
                              [(client.firstName, client.lastName)]
                                .filter(Boolean)
                                .join(" ")
                                .trim() ||
                              "Unnamed Client",
                          })) ?? []),
                        ]}
                      />
                    </Form.Item>
                  </div>
                </Form.Item>
              </div>

              <div className="space-y-2">
                <Form.Item dependencies={["clientId"]} noStyle>
                  {({ getFieldValue }) => (
                    <Form.Item
                      name="billable"
                      label="Billable Status"
                      className="mb-0"
                      valuePropName="checked"
                    >
                      <Checkbox disabled={!getFieldValue("clientId")}>
                        <div className="flex items-center space-x-2 cursor-pointer">
                          <Form.Item dependencies={["billable"]} noStyle>
                            {({ getFieldValue }) => (
                              <DollarSign
                                size={16}
                                className={
                                  getFieldValue("billable")
                                    ? "text-green-500"
                                    : "text-gray-400"
                                }
                              />
                            )}
                          </Form.Item>
                          <span>Make this task billable to client</span>
                        </div>
                      </Checkbox>
                    </Form.Item>
                  )}
                </Form.Item>
              </div>
            </div>
            <Form.Item dependencies={["billable", "clientId"]} noStyle>
              {({ getFieldValue }) =>
                getFieldValue("billable") ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start space-x-3">
                    <AlertCircle size={20} className="text-yellow-500 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-yellow-800">
                        This task will be billable to{" "}
                        {clients?.items.find(
                          (c) => c.id === getFieldValue("clientId")
                        )
                          ? clients.items.find(
                              (c) => c.id === getFieldValue("clientId")
                            )?.companyName ||
                            [
                              clients.items.find(
                                (c) => c.id === getFieldValue("clientId")
                              )?.firstName,
                              clients.items.find(
                                (c) => c.id === getFieldValue("clientId")
                              )?.lastName,
                            ]
                              .filter(Boolean)
                              .join(" ")
                              .trim() ||
                            "Unnamed Client"
                          : "the client"}
                      </p>
                      <p className="text-yellow-700 mt-1">
                        The task details and time tracking will be included in
                        client reporting and invoicing.
                      </p>
                    </div>
                  </div>
                ) : null
              }
            </Form.Item>
          </div>
        </div>

        {/* Related Items Section */}
        <div className="bg-white rounded-lg border shadow-sm mb-6">
          <div className="p-4 border-b">
            <h2 className="font-medium">Related Items</h2>
          </div>

          <div className="p-6 space-y-6">
            <div className="space-y-2">
              <Form.Item
                name="inventoryIds"
                label="Link to Inventory Items"
                className="mb-0"
              >
                <InventoriesSelect />
              </Form.Item>
            </div>

            <div className="space-y-2">
              <Form.Item name="files" label="Attachments" className="mb-0">
                <Attachments />
              </Form.Item>
            </div>
          </div>
        </div>

        {/* Task Preview Card */}
        <TaskPreview staffs={staffs?.items} clients={clients?.items} />
      </Form>
    </Modal>
  );
};
